/**
 * `/admin/revenue-analytics` — "Umsatz & Miete – Analyse".
 *
 * An ORCHESTRATOR. It owns the URL state, one request, the loading/error shell
 * and the tab switch; every figure is rendered by the section component that
 * owns that block, and none of them computes money.
 *
 * ONE REQUEST, NOT ONE PER TAB.
 * The backend composes B2 (expected), A1 (service-period attribution) and B1
 * (aging + cashflow) inside a single REPEATABLE READ snapshot, so all six tabs
 * describe one state of the world. Fetching per tab would throw that guarantee
 * away and let two tabs of the same page disagree. The tabs are therefore pure
 * client-side presentation.
 *
 * NOT THE LEGACY REVENUE PAGES.
 * `/admin/revenue` sums invoices in the browser and `/admin/prognose` reads the
 * room-loop forecast endpoint that can non-deterministically drop one of two
 * tenancies in a handover month. Neither is imported here. Both stay routed and
 * untouched — retiring them is a decision of its own.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { fetchRevenueAnalytics } from "../../api/adminData/revenueAnalytics";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import RevenueAnalyticsHeader from "./revenueAnalytics/RevenueAnalyticsHeader";
import RevenueCashflowCard from "./revenueAnalytics/RevenueCashflowCard";
import RevenueDataQualitySection, {
  hasAnyFindings,
  RevenueDataQualitySummaryCard,
} from "./revenueAnalytics/RevenueDataQualitySection";
import RevenueDistributionChart from "./revenueAnalytics/RevenueDistributionChart";
import RevenueForecastCard from "./revenueAnalytics/RevenueForecastCard";
import RevenueKpiGrid from "./revenueAnalytics/RevenueKpiGrid";
import RevenueReceivablesCard from "./revenueAnalytics/RevenueReceivablesCard";
import RevenueTopUnitsCard from "./revenueAnalytics/RevenueTopUnitsCard";
import RevenueTrendChart from "./revenueAnalytics/RevenueTrendChart";
import { periodLabel } from "./revenueAnalytics/revenueAnalyticsFormat";

const DEFAULT_TREND_MONTHS = 6;
/** Fixed for the MVP: the forecast table is a fixed-height block, not a control. */
const FORECAST_MONTHS = 3;

export const TABS = [
  { id: "overview", label: "Übersicht" },
  { id: "units", label: "Nach Einheit" },
  { id: "receivables", label: "Forderungen" },
  { id: "payments", label: "Zahlungen" },
  { id: "forecast", label: "Forecast" },
  { id: "quality", label: "Datenqualität" },
];

/**
 * Underline tabs on top of the Radix primitive.
 *
 * The primitive is used for the keyboard behaviour and the ARIA wiring; its
 * default pill skin is overridden through `cn`'s tailwind-merge, exactly as
 * `DetailTabs` already does — but with theme tokens rather than a hard-coded
 * accent, so the bar works in both themes.
 */
const TAB_LIST_CLASS =
  "h-auto w-full justify-start gap-6 overflow-x-auto rounded-none border-b border-border bg-transparent p-0";
const TAB_TRIGGER_CLASS =
  "-mb-px whitespace-nowrap rounded-none border-b-2 border-transparent px-1 py-3 text-sm font-medium text-muted-foreground " +
  "data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold " +
  "data-[state=active]:text-primary data-[state=active]:shadow-none hover:text-foreground";

function ErrorCard({ message, onRetry }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
      <p>{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-lg border border-red-300 px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-red-500/40"
        >
          Erneut versuchen
        </button>
      ) : null}
    </div>
  );
}

function CardSkeleton({ className = "h-64" }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-4 ${className}`}>
      <Skeleton className="h-4 w-44" />
      <Skeleton className="mt-4 h-3 w-full" />
      <Skeleton className="mt-2 h-3 w-5/6" />
      <Skeleton className="mt-2 h-3 w-2/3" />
    </div>
  );
}

/**
 * The loading shell.
 *
 * Skeletons in the FINAL geometry, and deliberately no numbers: a placeholder
 * "CHF 0" is a financial statement, and one that is wrong most of the time.
 */
function LoadingShell() {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Auswertung wird geladen …</span>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-7 w-32" />
            <Skeleton className="mt-2 h-3 w-28" />
            <Skeleton className="mt-3 h-[38px] w-full" />
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-[7fr_6fr_5fr]">
        <CardSkeleton className="h-80" />
        <CardSkeleton className="h-80" />
        <CardSkeleton className="h-80" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-[7fr_6fr_5fr]">
        <CardSkeleton className="h-72" />
        <CardSkeleton className="h-72" />
        <CardSkeleton className="h-72" />
      </div>
    </div>
  );
}

/** Reads a positive integer out of the URL, falling back when it is nonsense. */
function intParam(params, key, fallback) {
  const raw = params.get(key);
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function AdminRevenueAnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [activeTab, setActiveTab] = useState(TABS[0].id);

  const now = useMemo(() => new Date(), []);
  const year = intParam(searchParams, "year", now.getFullYear());
  const month = intParam(searchParams, "month", now.getMonth() + 1);
  const trendMonths = intParam(searchParams, "trendMonths", DEFAULT_TREND_MONTHS);

  // Comparison is all-or-nothing in the URL too: one half alone is treated as
  // "automatic", so a hand-edited link cannot produce a state the API rejects.
  const rawCompareYear = intParam(searchParams, "compareYear", null);
  const rawCompareMonth = intParam(searchParams, "compareMonth", null);
  const comparisonYear = rawCompareYear != null && rawCompareMonth != null ? rawCompareYear : null;
  const comparisonMonth = comparisonYear != null ? rawCompareMonth : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError("");

    fetchRevenueAnalytics({
      year,
      month,
      comparisonYear,
      comparisonMonth,
      trendMonths,
      forecastMonths: FORECAST_MONTHS,
      signal: controller.signal,
    })
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((e) => {
        if (cancelled || e?.name === "AbortError") return;
        // The previous period's figures are DROPPED rather than left on screen:
        // stale money under a fresh month heading is worse than no money.
        setData(null);
        setError(
          e?.status === 403
            ? "Für diese Auswertung fehlen die Berechtigungen. Sie ist Administratoren und Managern vorbehalten."
            : e?.message || "Die Auswertung konnte nicht geladen werden."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      // Cancels the in-flight request so a slow answer for the previous month
      // can never land after a newer one.
      controller.abort();
    };
  }, [year, month, comparisonYear, comparisonMonth, trendMonths, reloadToken]);

  const updateParams = useCallback(
    (mutate) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handlePeriodChange = useCallback(
    (y, m) => {
      updateParams((p) => {
        p.set("year", String(y));
        p.set("month", String(m));
      });
    },
    [updateParams]
  );

  const handleComparisonChange = useCallback(
    (y, m) => {
      updateParams((p) => {
        if (y == null || m == null) {
          p.delete("compareYear");
          p.delete("compareMonth");
          return;
        }
        p.set("compareYear", String(y));
        p.set("compareMonth", String(m));
      });
    },
    [updateParams]
  );

  const handleTrendMonthsChange = useCallback(
    (n) => {
      updateParams((p) => p.set("trendMonths", String(n)));
    },
    [updateParams]
  );

  // The comparison label always comes from the RESPONSE. With "Automatisch"
  // selected, only the backend knows which month it resolved to, and computing
  // "previous month" here would eventually disagree with the figures beside it.
  const comparisonLabel = data ? periodLabel(data.comparison_period) : "—";

  // A LENGTH, not a sum. `unattributed.mirror_invoices` is the response's own
  // complete list of invoices that belong to no rent month; counting it is the
  // same presentation step the quality strip below already performs. Nothing is
  // added, filtered by month, or turned into money.
  const unattributedInvoiceCount =
    data?.attribution?.unattributed?.mirror_invoices?.length ?? 0;

  const showQualityStrip =
    !!data &&
    (hasAnyFindings(data.data_quality, data.current_receivables) ||
      (data.attribution?.unattributed?.mirror_invoices?.length ?? 0) > 0 ||
      data.trust?.currency_detection_reliable !== true);

  return (
    <div className="mx-auto max-w-[1400px]">
      <RevenueAnalyticsHeader
        year={year}
        month={month}
        comparisonYear={comparisonYear}
        comparisonMonth={comparisonMonth}
        onPeriodChange={handlePeriodChange}
        onComparisonChange={handleComparisonChange}
        resolvedComparisonLabel={loading ? null : data ? comparisonLabel : null}
      />

      <div className="mt-5">
        {error ? (
          <ErrorCard message={error} onRetry={() => setReloadToken((t) => t + 1)} />
        ) : loading || !data ? (
          <LoadingShell />
        ) : (
          <>
            <RevenueKpiGrid
              kpis={data.kpis}
              comparison={data.comparison}
              comparisonLabel={comparisonLabel}
              trend={data.trend}
              unattributedInvoiceCount={unattributedInvoiceCount}
            />

            {showQualityStrip ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  Datenqualität: Es bestehen Hinweise, die einzelne Auswertungen einschränken können.
                </span>
                <button
                  type="button"
                  onClick={() => setActiveTab("quality")}
                  className="shrink-0 rounded-lg border border-amber-300 px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-amber-500/40"
                >
                  Details anzeigen
                </button>
              </div>
            ) : null}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className={TAB_LIST_CLASS} aria-label="Bereiche der Umsatzanalyse">
                {TABS.map((t) => (
                  <TabsTrigger key={t.id} value={t.id} className={TAB_TRIGGER_CLASS}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-[7fr_6fr_5fr]">
                  <RevenueTrendChart
                    trend={data.trend}
                    trendMonths={trendMonths}
                    onTrendMonthsChange={handleTrendMonthsChange}
                    unattributedInvoiceCount={unattributedInvoiceCount}
                  />
                  <RevenueDistributionChart
                    distribution={data.revenue_distribution}
                    selectedPeriod={data.selected_period}
                  />
                  <RevenueTopUnitsCard
                    units={data.top_units}
                    unassignedUnit={data.unassigned_unit}
                    comparisonLabel={comparisonLabel}
                  />
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-[7fr_6fr_5fr]">
                  <RevenueReceivablesCard receivables={data.current_receivables} />
                  <RevenueCashflowCard
                    cashflow={data.cashflow}
                    cashflowTrend={data.cashflow_trend}
                    selectedPeriod={data.selected_period}
                  />
                  <RevenueDataQualitySummaryCard
                    dataQuality={data.data_quality}
                    // One finding's count lives on the receivables block rather
                    // than under `data_quality` — see `findingSource`.
                    receivables={data.current_receivables}
                    onOpenDetails={() => setActiveTab("quality")}
                  />
                </div>

                <div className="mt-4">
                  <RevenueForecastCard forecast={data.forecast} />
                </div>
              </TabsContent>

              <TabsContent value="units" className="mt-4">
                <RevenueTopUnitsCard
                  units={data.top_units}
                  unassignedUnit={data.unassigned_unit}
                  comparisonLabel={comparisonLabel}
                />
              </TabsContent>

              <TabsContent value="receivables" className="mt-4">
                <RevenueReceivablesCard receivables={data.current_receivables} />
              </TabsContent>

              <TabsContent value="payments" className="mt-4">
                <RevenueCashflowCard
                  cashflow={data.cashflow}
                  cashflowTrend={data.cashflow_trend}
                  selectedPeriod={data.selected_period}
                />
              </TabsContent>

              <TabsContent value="forecast" className="mt-4">
                <RevenueForecastCard forecast={data.forecast} />
              </TabsContent>

              <TabsContent value="quality" className="mt-4">
                <RevenueDataQualitySection
                  dataQuality={data.data_quality}
                  attribution={data.attribution}
                  trust={data.trust}
                  receivables={data.current_receivables}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
